import type { TypesenseProduct } from "@/lib/typesense";
import type { PaginatedResponse } from "@medusajs/types";
import type { SalonStockerHttpTypes } from "@repo/api-sdk";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { queryKeys } from "./queryKeys";

/**
 * Optional fields: "external_id" | "meta"
 */
type ProductOptionalField =
  keyof SalonStockerHttpTypes.StoreProductPreviewOptionalFields;

/**
 * Query parameters with type-safe fields parameter.
 *
 */
export type FeaturedProductsQueryParams = Omit<
  SalonStockerHttpTypes.StoreFeaturedProductsParams,
  "fields"
>;

export function featuredProductsQuery(query?: FeaturedProductsQueryParams) {
  const defaultQuery = {
    limit: 50
  } satisfies FeaturedProductsQueryParams &
    Required<Pick<FeaturedProductsQueryParams, "limit">>;

  const finalQuery = { ...defaultQuery, ...query };

  return queryOptions({
    queryKey: queryKeys.featuredProducts(finalQuery),
    queryFn: async (): Promise<
      PaginatedResponse<{ products: TypesenseProduct[] }>
    > => {
      const { typesenseClient, PRODUCTS_INDEX } = await import(
        "@/lib/typesense"
      );

      const offset = finalQuery.offset ?? 0;
      const limit = finalQuery.limit;

      // Typesense uses 1-indexed pages, calculate from offset
      const page = Math.floor(offset / limit) + 1;

      const searchResults = await typesenseClient
        .collections<TypesenseProduct>(PRODUCTS_INDEX)
        .documents()
        .search({
          q: "*",
          query_by: "title",
          filter_by: "collection.handle:=featured",
          per_page: limit,
          page
        });

      const products: TypesenseProduct[] =
        searchResults.hits?.map((hit) => hit.document) || [];

      return {
        products,
        count: searchResults.found,
        limit,
        offset
      };
    }
  });
}

export function useFeaturedProducts(
  query?: FeaturedProductsQueryParams,
  options?: {
    enabled?: boolean;
  }
): ReturnType<
  typeof useQuery<PaginatedResponse<{ products: TypesenseProduct[] }>>
> {
  return useQuery({
    ...featuredProductsQuery(query),
    ...options
  });
}
